import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getRivhitCustomers, getRivhitItems, getRivhitConfig, createRivhitCustomer } from '../api/client';

export interface RivhitCustomer {
  customer_id: number;
  last_name: string;
  first_name: string;
  email?: string;
  phone?: string;
  street?: string;
  city?: string;
  id_number?: number;
  vat_number?: number;
  price_list_id?: number;
  discount_percent?: number;
}

export interface RivhitItem {
  item_id?: number;
  item_name?: string;
  catalog_number?: string;
  item_group_id?: number;
  item_group_name?: string;
  price?: number;
  cost?: number;
  quantity_type?: string;
  barcode?: string;
  is_active?: boolean;
}

interface RivhitDataContextValue {
  /** Whether a Rivhit API key has been configured in Settings */
  configured: boolean;
  /** True while checking if Rivhit is configured */
  configLoading: boolean;

  customers: RivhitCustomer[];
  customersLoading: boolean;
  /** Client-side fuzzy search across name, phone, email, city */
  searchCustomers: (query: string) => RivhitCustomer[];
  /** Load customer list once when a page actually needs it */
  ensureCustomersLoaded: () => Promise<void>;
  /** Force re-fetch from Rivhit API (bypasses cache) */
  refreshCustomers: () => Promise<void>;
  /** Create a new customer in Rivhit and refresh the list */
  createCustomer: (data: Omit<RivhitCustomer, 'customer_id'> & { last_name: string }) => Promise<number>;

  items: RivhitItem[];
  itemsLoading: boolean;
  /** Client-side fuzzy search across item name, catalog number, group name */
  searchItems: (query: string) => RivhitItem[];
  /** Load item list once when a page actually needs it */
  ensureItemsLoaded: () => Promise<void>;
  /** Force re-fetch from Rivhit API (bypasses cache) */
  refreshItems: () => Promise<void>;

  /** Re-check config and reload data (call after saving keys in Settings) */
  reloadAfterConfigChange: () => Promise<void>;
}

const RivhitDataContext = createContext<RivhitDataContextValue | null>(null);

/** Helper: compose display name from first + last */
export function rivhitDisplayName(c: RivhitCustomer): string {
  return [c.first_name, c.last_name].filter(Boolean).join(' ') || `#${c.customer_id}`;
}

function fuzzyMatch(query: string, target: string): boolean {
  return target.toLowerCase().includes(query.toLowerCase());
}

export function RivhitDataProvider({ children }: { children: React.ReactNode }) {
  const [configured, setConfigured] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [customers, setCustomers] = useState<RivhitCustomer[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [items, setItems] = useState<RivhitItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const fetchedRef = useRef(false);
  const customersRequestRef = useRef<Promise<void> | null>(null);
  const itemsRequestRef = useRef<Promise<void> | null>(null);

  const loadCustomers = useCallback(async () => {
    if (customersRequestRef.current) return customersRequestRef.current;

    const request = (async () => {
    setCustomersLoading(true);
    const t0 = performance.now();
    console.log(`[RivhitData] 👥 Loading Rivhit customers...`, { timestamp: new Date().toISOString() });
    try {
      const res = await getRivhitCustomers();
      const customers = res.data ?? [];
      console.log(`[RivhitData] ✅ Loaded ${customers.length} customers in ${(performance.now() - t0).toFixed(1)}ms`, { timestamp: new Date().toISOString() });
      setCustomers(customers);
    } catch (err) {
      console.error(`[RivhitData] ❌ Failed to load customers after ${(performance.now() - t0).toFixed(1)}ms:`, err, { timestamp: new Date().toISOString() });
    } finally {
      setCustomersLoading(false);
    }
    })();

    customersRequestRef.current = request;
    try {
      await request;
    } finally {
      customersRequestRef.current = null;
    }
  }, []);

  const loadItems = useCallback(async () => {
    if (itemsRequestRef.current) return itemsRequestRef.current;

    const request = (async () => {
    setItemsLoading(true);
    const t0 = performance.now();
    console.log(`[RivhitData] 📦 Loading Rivhit items...`, { timestamp: new Date().toISOString() });
    try {
      const res = await getRivhitItems();
      const items = res.data ?? [];
      console.log(`[RivhitData] ✅ Loaded ${items.length} items in ${(performance.now() - t0).toFixed(1)}ms`, { timestamp: new Date().toISOString() });
      setItems(items);
    } catch (err) {
      console.error(`[RivhitData] ❌ Failed to load items after ${(performance.now() - t0).toFixed(1)}ms:`, err, { timestamp: new Date().toISOString() });
    } finally {
      setItemsLoading(false);
    }
    })();

    itemsRequestRef.current = request;
    try {
      await request;
    } finally {
      itemsRequestRef.current = null;
    }
  }, []);

  // Check if Rivhit API key is configured. Data itself loads lazily per-page.
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    (async () => {
      try {
        const res = await getRivhitConfig();
        const hasToken = res.data?.has_token === true;
        setConfigured(hasToken);
        if (hasToken) {
          console.log('[RivhitData] 🔑 Rivhit configured');
        } else {
          console.log('[RivhitData] ⚠️ Rivhit not configured — skipping data load. Set API key in Settings.');
        }
      } catch (err) {
        console.warn('[RivhitData] Could not check Rivhit config:', err);
        setConfigured(false);
      } finally {
        setConfigLoading(false);
      }
    })();
  }, []);

  // Called after saving new Rivhit keys in Settings — re-check and reload
  const reloadAfterConfigChange = useCallback(async () => {
    try {
      const res = await getRivhitConfig();
      const hasToken = res.data?.has_token === true;
      setConfigured(hasToken);
      if (hasToken) {
        setCustomers([]);
        setItems([]);
      } else {
        setCustomers([]);
        setItems([]);
      }
    } catch {
      setConfigured(false);
    }
  }, [loadCustomers, loadItems]);

  const ensureCustomersLoaded = useCallback(async () => {
    if (!configured || customers.length > 0 || customersLoading) return;
    await loadCustomers();
  }, [configured, customers.length, customersLoading, loadCustomers]);

  const ensureItemsLoaded = useCallback(async () => {
    if (!configured || items.length > 0 || itemsLoading) return;
    await loadItems();
  }, [configured, items.length, itemsLoading, loadItems]);

  const searchCustomersFn = useCallback(
    (query: string): RivhitCustomer[] => {
      if (!query.trim()) return customers;
      const q = query.trim();
      return customers.filter((c) => {
        const fields = [
          c.first_name,
          c.last_name,
          rivhitDisplayName(c),
          c.phone,
          c.email,
          c.city,
          c.street,
          c.id_number != null ? String(c.id_number) : undefined,
        ].filter(Boolean) as string[];
        return fields.some((f) => fuzzyMatch(q, f));
      });
    },
    [customers],
  );

  const searchItemsFn = useCallback(
    (query: string): RivhitItem[] => {
      if (!query.trim()) return items;
      const q = query.trim();
      return items.filter((item) => {
        const fields = [
          item.item_name,
          item.catalog_number,
          item.item_group_name,
          item.barcode,
        ].filter(Boolean) as string[];
        return fields.some((f) => fuzzyMatch(q, f));
      });
    },
    [items],
  );

  const forceRefreshCustomers = useCallback(() => loadCustomers(), [loadCustomers]);
  const forceRefreshItems = useCallback(() => loadItems(), [loadItems]);

  const createCustomerFn = useCallback(async (data: Omit<RivhitCustomer, 'customer_id'> & { last_name: string }): Promise<number> => {
    const res = await createRivhitCustomer(data);
    const customerId = res.data.customer_id;
    // Refresh customer list to include the new customer
    await loadCustomers();
    return customerId;
  }, [loadCustomers]);

  const value = useMemo<RivhitDataContextValue>(
    () => ({
      configured,
      configLoading,
      customers,
      customersLoading,
      searchCustomers: searchCustomersFn,
      ensureCustomersLoaded,
      refreshCustomers: forceRefreshCustomers,
      createCustomer: createCustomerFn,
      items,
      itemsLoading,
      searchItems: searchItemsFn,
      ensureItemsLoaded,
      refreshItems: forceRefreshItems,
      reloadAfterConfigChange,
    }),
    [configured, configLoading, customers, customersLoading, searchCustomersFn, ensureCustomersLoaded, forceRefreshCustomers, createCustomerFn, items, itemsLoading, searchItemsFn, ensureItemsLoaded, forceRefreshItems, reloadAfterConfigChange],
  );

  return <RivhitDataContext.Provider value={value}>{children}</RivhitDataContext.Provider>;
}

export function useRivhitData(): RivhitDataContextValue {
  const ctx = useContext(RivhitDataContext);
  if (!ctx) throw new Error('useRivhitData must be used within RivhitDataProvider');
  return ctx;
}
